import {
  useRef,
  useEffect,
  useState,
  type FC,
  type MutableRefObject,
} from 'react';
import Quill, { type QuillOptions } from 'quill';
import { normalizeLinksInElement } from 'utils/htmlHelpers';
import { useLinkTooltip } from 'infrastructure/hooks/use-link-tooltip';

export const emptyValue = '<p><br></p>';

const theme = 'snow';

const modules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ color: [] }, { background: [] }],
    ['link'],
    ['clean'],
  ],
};

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'indent',
  'link',
  'color',
  'background',
];

export interface Props {
  value: string;
  height?: string;
  setValue: (value: string) => void;
  disabled: boolean;
}

const ReactQuill: FC<Props> = ({ setValue, value, disabled, height }) => {
  const divRef = useRef<HTMLDivElement>(null);
  const quillRef: MutableRefObject<Quill | null> = useRef(null);
  const [quillInstance, setQuillInstance] = useState<Quill | null>(null);

  useEffect(() => {
    if (divRef.current) {
      const opts: QuillOptions = {
        modules,
        formats,
        theme,
      };
      const quill = new Quill(divRef.current, opts);
      quillRef.current = quill;
      setQuillInstance(quill);

      quill.on(Quill.events.TEXT_CHANGE, () => {
        normalizeLinksInElement(quill.root);
        if (quill.root.innerHTML === emptyValue) setValue('');
        else setValue(quill.root.innerHTML);
      });

      normalizeLinksInElement(quill.root);
    }
  }, []);

  useLinkTooltip({
    containerRef: divRef,
    quill: quillInstance,
    offset: 10,
  });

  useEffect(() => {
    const currentValue = quillRef.current?.root.innerHTML;
    if ((currentValue === emptyValue || !currentValue) && value) {
      quillRef.current?.clipboard.dangerouslyPasteHTML(value);
    }
  }, [value]);

  useEffect(() => {
    quillRef.current?.enable(!disabled);
  }, [quillRef, disabled]);

  useEffect(() => {
    if (height && divRef.current) {
      const editorElement = divRef.current.querySelector(
        '.ql-editor',
      ) as HTMLDivElement;

      if (editorElement) {
        editorElement.style.maxHeight = height;
      }
    }
  }, [height, divRef]);

  return <div ref={divRef} />;
};

export default ReactQuill;
